import { SaveButton } from '@modules/analysis/components/SaveAnalysisButton/SaveButton';
import { SaveAnalysisForm } from '@modules/analysis/components/SaveAnalysisForm';
import { useDashboardToAddToFromParam } from '@modules/analysis/hooks';
import { useSaveAnalysis } from '@modules/analysis/hooks/useSaveAnalysis';
import { AnalysisFormProps } from '@modules/analysis/types';
import { getAnalysisURL } from '@modules/bookmark/helpers';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { SaveArtefactConfirmDialog } from '@modules/common/components/SaveArtefact/SaveArtefactConfirmDialog';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import { directionToString, scrollToPageBottom } from '@modules/common/utils';
import {
  getDashboardLink,
  getDashboardRoute,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { DashboardHeader } from '@modules/dashboard/types';
import {
  AnalysisMetadataInput,
  AnalysisUseCase,
  AnalysisViewFragment,
  DashboardViewFragment,
} from 'generated/graphql';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnalysisV3EventProps } from 'v2/analytics/events';
import { Scope } from 'v2/core/entities/scope';
import { EQLProps } from 'v2/core/repository/eql/types';
import { useGetUser } from 'v2/core/services/user';

export interface SaveAnalysisButtonProps {
  isDisabled: boolean;
  eqlProps: Omit<EQLProps, 'sourceDetailsMap'>;
  metadata: string;
  onAnalysisCreate?: (
    analysis?: Pick<AnalysisViewFragment, 'title' | 'viewToken' | 'useCase'>,
    dashboard?: Pick<DashboardViewFragment, 'title' | 'viewToken'>,
  ) => void;
  analysisFormProps?: AnalysisFormProps;
  useCase: AnalysisUseCase;
  eventProps?: Partial<Omit<AnalysisV3EventProps, 'eventName'>>;
  className?: string;
  metadataV2?: AnalysisMetadataInput;
}

export const SaveAnalysisButton = ({
  isDisabled,
  eqlProps,
  metadata,
  onAnalysisCreate,
  analysisFormProps,
  eventProps,
  useCase,
  className,
  metadataV2,
}: SaveAnalysisButtonProps) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [savedAnalysisRoute, setSavedAnalysisRoute] = useState<null | string>(
    null,
  );

  const { showSuccessNotification } = useNotificationContext();

  const navigate = useNavigate();

  const { dashboardToAddToFromParam, cellInsertDirection } =
    useDashboardToAddToFromParam();

  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const onCreate = (
    analysis?: Pick<AnalysisViewFragment, 'title' | 'viewToken' | 'useCase'>,
    dashboard?: Pick<DashboardViewFragment, 'title' | 'viewToken'>,
  ) => {
    onAnalysisCreate?.(analysis, dashboard);
    if (!dashboardToAddToFromParam || !analysis) {
      return;
    }
    navigate(getDashboardRoute(dashboardToAddToFromParam));
    showSuccessNotification({
      contentComponent: `${analysis?.title ?? 'This Quantify'} is added to "${
        dashboardToAddToFromParam.title
      }"`,
      props: {
        isClosable: true,
        timeout: NOTIFICATION_TIMEOUT_LONG_6S,
      },
    });

    trackDashboardEvent({
      eventName: 'Dashboard Add Content',
      placement: directionToString(cellInsertDirection),
      type: 'analysis',
      from: 'Analysis Page',
    });

    trackDashboardEvent({
      eventName: 'Analysis Added to Dashboard',
      dashboardLink: getDashboardLink(dashboardToAddToFromParam, tenantSlug),
      analysisLink: getAnalysisURL(analysis, tenantSlug),
      from: 'Analysis Page',
    });
    setTimeout(scrollToPageBottom, 1000);
  };

  const { onSaveAnalysis, isLoading } = useSaveAnalysis({
    eqlProps,
    metadata,
    shouldRedirect: false,
    onAnalysisCreate: onCreate,
    eventProps,
    useCase,
    metadataV2,
  });

  const openForm = () => setIsFormOpen(true);

  const closeForm = () => setIsFormOpen(false);

  const onSubmitAnalysisForm = async (
    name: string,
    scope: Scope,
    newDashboard?: DashboardHeader,
  ) => {
    const analysisDetails = await onSaveAnalysis(name, scope, newDashboard);
    setSavedAnalysisRoute(analysisDetails.artefactRoute);

    return analysisDetails;
  };

  return (
    <>
      <SaveButton
        isDisabled={isDisabled}
        isLoading={isLoading}
        onSave={openForm}
        className={className}
      />
      <SaveArtefactConfirmDialog
        artefactRoute={savedAnalysisRoute}
        fullWidth
        maxWidth="sm"
        PaperProps={{ elevation: 3 }}
        open={isFormOpen}
        onClose={closeForm}
      >
        <SaveAnalysisForm
          onSubmit={onSubmitAnalysisForm}
          onClose={closeForm}
          dashboardToAddTo={dashboardToAddToFromParam}
          analysisFormProps={analysisFormProps}
        />
      </SaveArtefactConfirmDialog>
    </>
  );
};
